import {
  ACTIONS,
  type Action,
  type ApprovalRule,
  diffApprovalMatrices,
  diffPermissionMatrices,
  MODULES,
  type Module,
  type Scope,
} from '@felix/domain';
import {
  Button,
  FileButton,
  Modal,
  PasswordInput,
  SegmentedControl,
  Select,
  Tabs,
  Textarea,
} from '@mantine/core';
import {
  IconDownload,
  IconFileSpreadsheet,
  IconLock,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge } from '../../StatusBadge.js';
import table from '../materials/Materials.module.css';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { ApprovalRuleDialog, ApprovalRulesTable } from './ApprovalRulesTable.js';
import { MatrixDiff } from './MatrixDiff.js';
import {
  addSubject,
  cellState,
  draftChanges,
  draftFrom,
  type GridPosition,
  gridKey,
  type MatrixDraft,
  moduleGrants,
  nextScope,
  removeSubject,
  SCOPE_CYCLE,
  saveFormErrors,
  setScope,
  subjectOptions,
} from './matrix-model.js';
import { roleLabel, setupErrorText } from './RolesScreen.js';
import classes from './Setup.module.css';
import { type SetupSource, setupErrorCode, setupErrorDetail } from './setup-source.js';
import type {
  MatrixData,
  MatrixDiffData,
  MatrixImportPreview,
  MatrixRoleItem,
  MatrixSubjects,
  MatrixVersionItem,
  TemporaryPasswordDrop,
} from './setup-types.js';
import { TemporaryPasswordDroppedNotice } from './TemporaryPasswordDropped.js';

export interface PermissionGridProps {
  draft: MatrixDraft;
  roles: readonly MatrixRoleItem[];
  module: Module;
  /** ISM formu konu satırı; yoksa modül hücreleri. */
  subject: string | undefined;
  editable: boolean;
  onChange: (role: MatrixRoleItem, action: Action, scope: Scope) => void;
}

/**
 * Yetki ızgarası (ADR 0042 §24) [sorular #249]: satırlar roller, sütunlar işlemler (seçili modül ve
 * konu). Hücre tıklama ya da Space/Enter ile `none → own → vessel → fleet` döner; oklar, Home ve
 * End hücreler arasında gezer (WAI-ARIA grid, tek sekme durağı). Verilemeyen hücre kilitli ve
 * kaynağıyla açıklamalı; ilk sütun sabit, ızgara kendi kabında kayar.
 */
export function PermissionGrid({
  draft,
  roles,
  module,
  subject,
  editable,
  onChange,
}: PermissionGridProps) {
  const { t } = useTranslation();
  const m = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.matrix.${key}`, options);
  const [focus, setFocus] = useState<GridPosition>({ row: 0, col: 0 });
  const buttons = useRef(new Map<string, HTMLButtonElement>());
  const size = { rows: roles.length, cols: ACTIONS.length };
  const position = {
    row: Math.min(focus.row, Math.max(0, size.rows - 1)),
    col: Math.min(focus.col, size.cols - 1),
  };
  const moduleName = m(`moduleNames.${module}`);
  return (
    <div className={classes.gridWrap}>
      <table
        className={classes.grid}
        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: WAI-ARIA grid; hücrelerdeki düğmeler odaklanır (APG "Data Grid")
        role="grid"
        aria-label={m('grid', { module: moduleName })}
        aria-readonly={editable ? undefined : true}
      >
        <thead>
          <tr>
            <th scope="col" className={classes.gridRole}>
              {m('role')}
            </th>
            {ACTIONS.map((action) => (
              <th key={action} scope="col">
                {m(`actions.${action}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {roles.map((role, row) => (
            <tr key={role.id}>
              <th scope="row" className={classes.gridRole} title={roleLabel(t, role)}>
                {roleLabel(t, role)}
              </th>
              {ACTIONS.map((action, col) => {
                const state = cellState(draft, roles, role.id, module, action, subject);
                const scopeText = m(`scope.${state.scope}`);
                const label = m('cellLabel', {
                  role: roleLabel(t, role),
                  action: m(`actions.${action}`),
                  scope: scopeText,
                });
                const focused = position.row === row && position.col === col;
                const cycle = () => {
                  if (!editable || state.locked) return;
                  onChange(role, action, nextScope(state.scope));
                };
                return (
                  // biome-ignore lint/a11y/useFocusableInteractive: odak hücredeki düğmede (roving tabindex)
                  <td
                    key={action}
                    // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: WAI-ARIA grid hücresi
                    role="gridcell"
                    aria-readonly={!editable && !state.locked ? true : undefined}
                  >
                    <button
                      type="button"
                      ref={(element) => {
                        if (element) buttons.current.set(`${row}:${col}`, element);
                        else buttons.current.delete(`${row}:${col}`);
                      }}
                      className={classes.scope}
                      data-scope={state.scope}
                      data-warn={state.justification ? true : undefined}
                      aria-label={
                        state.locked ? `${label}. ${m('locked', { source: state.locked })}` : label
                      }
                      title={
                        state.locked
                          ? m('locked', { source: state.locked })
                          : state.justification
                            ? m('justified')
                            : undefined
                      }
                      aria-disabled={state.locked ? true : undefined}
                      tabIndex={focused ? 0 : -1}
                      onFocus={() => setFocus({ row, col })}
                      onClick={cycle}
                      onKeyDown={(event) => {
                        const next = gridKey(event.key, { row, col }, size);
                        if (next === null) return;
                        event.preventDefault();
                        if (next === 'cycle') {
                          cycle();
                          return;
                        }
                        setFocus(next);
                        buttons.current.get(`${next.row}:${next.col}`)?.focus();
                      }}
                    >
                      {state.locked ? <IconLock size={12} aria-hidden /> : null}
                      {m(`scopeShort.${state.scope}`)}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface SaveMatrixFormProps {
  /** Yeni sürümün numarası. */
  version: number;
  changes: number;
  /** Temel sürüm eskidi (409 `MATRIX_VERSION_CONFLICT`). */
  conflict: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onReload: () => void;
  onSave: (reason: string, password: string) => Promise<void>;
}

/**
 * Kaydetme formu (ADR 0042 §24): gerekçe ve parola zorunlu; çakışmada sessiz ezme yok, son sürümü
 * yükleme seçeneği.
 */
export function SaveMatrixForm({
  version,
  changes,
  conflict,
  busy,
  error,
  onCancel,
  onReload,
  onSave,
}: SaveMatrixFormProps) {
  const { t } = useTranslation();
  const s = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.matrix.save.${key}`, options);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const invalid = Object.keys(saveFormErrors({ reason, password })).length > 0;
  if (conflict) {
    return (
      <div>
        <p className={classes.notice} data-tone="warning" role="alert">
          {s('conflict')}
        </p>
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onCancel}>
            {s('cancel')}
          </Button>
          <Button onClick={onReload}>{s('reload')}</Button>
        </div>
      </div>
    );
  }
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (!invalid) void onSave(reason, password);
      }}
    >
      <p className={classes.muted}>
        {s('body', { version })} {t('setup.matrix.summary.changesValue', { count: changes })}
      </p>
      <Textarea
        label={s('reason')}
        required
        value={reason}
        maxLength={2000}
        autosize
        minRows={2}
        onChange={(event) => setReason(event.currentTarget.value)}
        mb="sm"
      />
      <PasswordInput
        label={s('password')}
        required
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.currentTarget.value)}
      />
      {error ? (
        <p className={classes.error} role="alert">
          {error}
        </p>
      ) : null}
      <div className={classes.dialogActions}>
        <Button variant="default" onClick={onCancel}>
          {s('cancel')}
        </Button>
        <Button type="submit" loading={busy} disabled={invalid}>
          {s('confirm')}
        </Button>
      </div>
    </form>
  );
}

type Tab = 'permissions' | 'approvals' | 'history';
type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; data: MatrixData };

/** Konunun okunur adı: klasör kodu ve adı ya da form no ve başlığı; bilinmeyen konu kendisi. */
function subjectLabeler(subjects: MatrixSubjects | null) {
  return (subject: string) => {
    if (!subjects) return subject;
    if (subject.startsWith('folder:')) {
      const folder = subjects.folders.find((f) => `folder:${f.key}` === subject);
      return folder ? `${folder.code ? `${folder.code} ` : ''}${folder.name}` : subject;
    }
    const doc = subjects.documents.find((d) => `document:${d.id}` === subject);
    return doc
      ? `${doc.docNo ? `${doc.docNo} ` : ''}${doc.title}${doc.vessel ? ` (${doc.vessel.name})` : ''}`
      : subject;
  };
}

function SubjectDialog({
  subjects,
  used,
  onClose,
  onAdd,
}: {
  subjects: MatrixSubjects | null;
  used: string[];
  onClose: () => void;
  onAdd: (subject: string) => void;
}) {
  const { t } = useTranslation();
  const d = (key: string) => t(`setup.matrix.subjectDialog.${key}`);
  const [kind, setKind] = useState<'folder' | 'document'>('folder');
  const [value, setValue] = useState<string | null>(null);
  const options = subjects ? subjectOptions(subjects, used) : { folders: [], documents: [] };
  const list = kind === 'folder' ? options.folders : options.documents;
  const data =
    kind === 'folder'
      ? list
      : Object.entries(
          list.reduce<Record<string, { value: string; label: string }[]>>((groups, option) => {
            const group = option.group ?? d('companyForm');
            groups[group] = [
              ...(groups[group] ?? []),
              { value: option.value, label: option.label },
            ];
            return groups;
          }, {}),
        ).map(([group, items]) => ({ group, items }));
  return (
    <Modal
      opened
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={d('title')}
    >
      <SegmentedControl
        fullWidth
        aria-label={d('kind')}
        value={kind}
        onChange={(next) => {
          setKind(next as 'folder' | 'document');
          setValue(null);
        }}
        data={[
          { value: 'folder', label: d('folder') },
          { value: 'document', label: d('document') },
        ]}
        mb="sm"
      />
      {list.length === 0 ? (
        <p className={classes.muted}>{d('empty')}</p>
      ) : (
        <Select
          label={kind === 'folder' ? d('folderLabel') : d('documentLabel')}
          data={data}
          value={value}
          searchable
          onChange={setValue}
          comboboxProps={{ withinPortal: true }}
        />
      )}
      <div className={classes.dialogActions}>
        <Button variant="default" onClick={onClose}>
          {d('cancel')}
        </Button>
        <Button disabled={!value} onClick={() => value && onAdd(value)}>
          {d('add')}
        </Button>
      </div>
    </Modal>
  );
}

function JustificationDialog({
  role,
  action,
  onClose,
  onSave,
}: {
  role: string;
  action: string;
  onClose: () => void;
  onSave: (text: string) => void;
}) {
  const { t } = useTranslation();
  const j = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.matrix.justification.${key}`, options);
  const [text, setText] = useState('');
  return (
    <Modal
      opened
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('setup.close') }}
      title={j('title')}
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (text.trim()) onSave(text.trim());
        }}
      >
        <p className={classes.muted}>{j('body', { role, action })}</p>
        <Textarea
          label={j('label')}
          required
          autosize
          minRows={3}
          maxLength={2000}
          value={text}
          onChange={(event) => setText(event.currentTarget.value)}
        />
        <div className={classes.dialogActions}>
          <Button variant="default" onClick={onClose}>
            {j('cancel')}
          </Button>
          <Button type="submit" disabled={text.trim() === ''}>
            {j('save')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function HistoryTab({
  source,
  roles,
  current,
  labeler,
}: {
  source: SetupSource;
  roles: readonly MatrixRoleItem[];
  current: number;
  labeler: (subject: string) => string;
}) {
  const { t } = useTranslation();
  const h = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.matrix.history.${key}`, options);
  const [versions, setVersions] = useState<MatrixVersionItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState<string | null>(current > 1 ? String(current - 1) : null);
  const [to, setTo] = useState<string | null>(String(current));
  const [diff, setDiff] = useState<MatrixDiffData | null>(null);
  const load = useCallback(async () => {
    try {
      setVersions(await source.matrixVersions());
    } catch (caught) {
      setError(setupErrorCode(caught));
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);
  const compare = async (a: number, b: number) => {
    setFrom(String(a));
    setTo(String(b));
    try {
      setDiff(await source.matrixDiff(a, b));
    } catch (caught) {
      setError(setupErrorCode(caught));
    }
  };
  const options = (versions ?? []).map((v) => ({
    value: String(v.version),
    label: `v${v.version}`,
  }));
  return (
    <div className={classes.screen}>
      <Panel flush title={h('table')} divider>
        {error ? (
          <LoadError code={error} onRetry={() => void load()} />
        ) : versions === null ? (
          <TableSkeleton columns={5} label={h('table')} />
        ) : (
          <section className={table.tableWrap}>
            <table className={`${table.table} ${classes.tableWide} ${classes.tallRows}`}>
              <colgroup>
                <col className={classes.w10} />
                <col className={classes.w18} />
                <col className={classes.w20} />
                <col className={classes.w30} />
                <col className={classes.w22} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col" className={classes.key}>
                    {h('columns.version')}
                  </th>
                  <th scope="col">{h('columns.at')}</th>
                  <th scope="col">{h('columns.by')}</th>
                  <th scope="col">{h('columns.reason')}</th>
                  <th scope="col" data-numeric>
                    {h('columns.compare')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {versions.map((version) => (
                  <tr key={version.version}>
                    <td className={classes.key}>v{version.version}</td>
                    <td>{formatDateTime(version.createdAt)}</td>
                    <td>{version.createdBy?.name ?? t('setup.matrix.summary.system')}</td>
                    <td title={version.reason}>{version.reason}</td>
                    <td data-numeric>
                      {version.version > 1 ? (
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={() => void compare(version.version - 1, version.version)}
                        >
                          {h('compare')}
                        </Button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </Panel>
      <Panel title={t('setup.matrix.diff.title')} divider>
        <div className={classes.filters}>
          <Select label={h('from')} w={140} data={options} value={from} onChange={setFrom} />
          <Select label={h('to')} w={140} data={options} value={to} onChange={setTo} />
          <Button
            variant="light"
            disabled={!from || !to || from === to}
            onClick={() => void compare(Number(from), Number(to))}
          >
            {h('show')}
          </Button>
        </div>
        {diff ? (
          <div className={classes.spaced}>
            <MatrixDiff
              roles={roles}
              permissions={diff.permissions}
              approvals={diff.approvals}
              subjectLabel={labeler}
            />
          </div>
        ) : null}
      </Panel>
    </div>
  );
}

/** Güncel sürümün özeti ve kaydetme eylemleri (geniş ekranda sağda, dar ekranda üstte). */
function VersionSummary({
  data,
  changes,
  editable,
  onDiscard,
  onSave,
}: {
  data: MatrixData;
  changes: number;
  editable: boolean;
  onDiscard: () => void;
  onSave: () => void;
}) {
  const { t } = useTranslation();
  const m = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.matrix.summary.${key}`, options);
  return (
    <Panel title={m('title')} divider>
      <dl className={classes.versionFacts}>
        <div>
          <dt>{m('base')}</dt>
          <dd>{m('baseValue', { version: data.version, at: formatDateTime(data.createdAt) })}</dd>
        </div>
        <div>
          <dt>{m('by')}</dt>
          <dd>{data.createdBy?.name ?? m('system')}</dd>
        </div>
        <div>
          <dt>{m('reason')}</dt>
          <dd>{data.reason}</dd>
        </div>
        <div>
          <dt>{m('changes')}</dt>
          <dd>
            {changes === 0 ? (
              <span className={classes.muted}>{m('noChanges')}</span>
            ) : (
              <StatusBadge tone="warning">{m('changesValue', { count: changes })}</StatusBadge>
            )}
          </dd>
        </div>
      </dl>
      {editable ? (
        <div className={classes.panelActions}>
          <Button variant="default" disabled={changes === 0} onClick={onDiscard}>
            {m('discard')}
          </Button>
          <Button disabled={changes === 0} onClick={onSave}>
            {m('save')}
          </Button>
        </div>
      ) : null}
    </Panel>
  );
}

/**
 * Yetki ve onay matrisi editörü (`/settings/matrices`; ADR 0042 §24) [sorular #249]: modül
 * seçici ve rol × işlem ızgarası (ISM formu konu satırlarıyla), onay kuralları tablosu, sürüm
 * geçmişi ve fark, Excel dışa ve içe aktarma. Kayıt: iki matris birlikte yeni sürüm, gerekçe ve
 * parolayla; eski temel sürümde "yeniden yükle".
 */
export function MatrixEditor({
  source,
  initialTab = 'permissions',
}: {
  source: SetupSource;
  initialTab?: Tab;
}) {
  const { t } = useTranslation();
  const m = (key: string, options: Record<string, unknown> = {}) =>
    t(`setup.matrix.${key}`, options);
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [draft, setDraft] = useState<MatrixDraft | null>(null);
  const [tab, setTab] = useState<Tab>(initialTab);
  const [module, setModule] = useState<Module>('ism_forms');
  const [subject, setSubject] = useState<string | undefined>(undefined);
  const [subjects, setSubjects] = useState<MatrixSubjects | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);
  // Rolü yönetici rolü olunca geçici parolası iptal edilen kişiler (ADR 0150 §6): sayı gösterilir
  const [dropped, setDropped] = useState<TemporaryPasswordDrop[]>([]);
  const [justify, setJustify] = useState<{
    role: MatrixRoleItem;
    action: Action;
    scope: Scope;
  } | null>(null);
  const [addingSubject, setAddingSubject] = useState(false);
  const [rule, setRule] = useState<{ index: number | null } | null>(null);
  const [saving, setSaving] = useState<{
    conflict: boolean;
    busy: boolean;
    error: string | null;
  } | null>(null);
  const [preview, setPreview] = useState<MatrixImportPreview | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await source.matrix();
      setState({ kind: 'ready', data });
      setDraft(draftFrom(data));
      setSubject(undefined);
    } catch (error) {
      setState({ kind: 'error', code: setupErrorCode(error) });
    }
  }, [source]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    source
      .matrixSubjects()
      .then(setSubjects)
      .catch(() => setSubjects({ folders: [], documents: [] }));
  }, [source]);

  const data = state.kind === 'ready' ? state.data : null;
  const roles = data?.roles ?? [];
  const editable = data?.canEdit ?? false;
  const changes = data && draft ? draftChanges(data, draft) : 0;
  const labeler = useMemo(() => subjectLabeler(subjects), [subjects]);

  const apply = (role: MatrixRoleItem, action: Action, scope: Scope, justification?: string) => {
    if (!draft) return;
    const result = setScope(draft, role, module, action, subject, scope, justification);
    if ('draft' in result) setDraft(result.draft);
    else if ('needsJustification' in result) setJustify({ role, action, scope });
  };

  const save = async (reason: string, password: string) => {
    if (!data || !draft) return;
    setSaving({ conflict: false, busy: true, error: null });
    try {
      const result = await source.saveMatrix({
        baseVersion: data.version,
        reason,
        password,
        permissions: draft.cells,
        approvals: draft.rules,
        justifications: draft.justifications,
      });
      setSaving(null);
      setNotice({ tone: 'success', text: m('save.saved', { version: result.version }) });
      setDropped(result.temporaryPasswordDropped ?? []);
      await load();
    } catch (error) {
      const code = setupErrorCode(error);
      setSaving({
        conflict: code === 'MATRIX_VERSION_CONFLICT',
        busy: false,
        error: setupErrorText(t, code, setupErrorDetail(error)),
      });
    }
  };

  const importFile = async (file: File | null) => {
    if (!file) return;
    setImportError(null);
    try {
      setPreview(await source.importMatrix(file));
    } catch (error) {
      setImportError(setupErrorText(t, setupErrorCode(error)));
    }
  };

  const ismSubjects = draft?.subjects ?? [];
  const saveDiff =
    data && draft
      ? {
          permissions: diffPermissionMatrices(
            {
              version: data.version,
              cells: data.permission.cells,
              justifications: data.permission.justifications,
            },
            { version: data.version, cells: draft.cells, justifications: draft.justifications },
          ),
          approvals: diffApprovalMatrices(
            { version: data.version, rules: data.approval.rules },
            { version: data.version, rules: draft.rules },
          ),
        }
      : null;

  return (
    <div className={classes.screen}>
      <PageHeader
        title={m('title')}
        description={m('description')}
        crumb={m('crumb')}
        actions={
          data ? (
            <>
              <Button
                component="a"
                variant="default"
                leftSection={<IconDownload size={16} />}
                href={source.matrixExportUrl(data.version)}
                download={`matrix-v${data.version}.xlsx`}
              >
                {m('summary.export')}
              </Button>
              {editable ? (
                <FileButton
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xlsx"
                  onChange={(file) => void importFile(file)}
                >
                  {(props) => (
                    <Button
                      {...props}
                      variant="default"
                      leftSection={<IconFileSpreadsheet size={16} />}
                    >
                      {m('summary.import')}
                    </Button>
                  )}
                </FileButton>
              ) : null}
            </>
          ) : undefined
        }
      />
      {notice ? (
        <p
          className={classes.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      <TemporaryPasswordDroppedNotice dropped={dropped} count />
      {importError ? (
        <p className={classes.notice} data-tone="danger" role="alert">
          {importError}
        </p>
      ) : null}
      {data && !editable ? (
        <p className={classes.notice} data-tone="info">
          {m('readOnly')}
        </p>
      ) : null}
      {state.kind === 'error' ? (
        <Panel>
          <LoadError title={m('loadError')} code={state.code} onRetry={() => void load()} />
        </Panel>
      ) : !data || !draft ? (
        <Panel flush>
          <TableSkeleton columns={9} label={m('title')} />
        </Panel>
      ) : (
        <div className={classes.editorLayout}>
          <div className={classes.versionAside}>
            <VersionSummary
              data={data}
              changes={changes}
              editable={editable}
              onDiscard={() => setDraft(draftFrom(data))}
              onSave={() => setSaving({ conflict: false, busy: false, error: null })}
            />
          </div>
          <Tabs
            className={classes.editorMain}
            value={tab}
            onChange={(value) => setTab((value ?? 'permissions') as Tab)}
            keepMounted={false}
          >
            <Tabs.List mb="md">
              <Tabs.Tab value="permissions">{m('tabs.permissions')}</Tabs.Tab>
              <Tabs.Tab value="approvals">{m('tabs.approvals')}</Tabs.Tab>
              <Tabs.Tab value="history">{m('tabs.history')}</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="permissions">
              <div className={classes.editor}>
                <Panel title={m('modules')} divider flush className={classes.modulesPanel}>
                  <ul className={classes.modules}>
                    {MODULES.map((item) => (
                      <li key={item}>
                        <button
                          type="button"
                          className={classes.moduleButton}
                          aria-current={item === module ? 'true' : undefined}
                          onClick={() => {
                            setModule(item);
                            setSubject(undefined);
                          }}
                        >
                          <span>{m(`moduleNames.${item}`)}</span>
                          <span className={classes.moduleCount}>
                            {m('moduleGranted', { count: moduleGrants(draft, item) })}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </Panel>
                <Panel flush aria-label={m('grid', { module: m(`moduleNames.${module}`) })}>
                  <div className={classes.gridHead}>
                    <Select
                      className={classes.moduleSelect}
                      label={m('modules')}
                      data={MODULES.map((item) => ({
                        value: item,
                        label: m(`moduleNames.${item}`),
                      }))}
                      value={module}
                      allowDeselect={false}
                      onChange={(value) => {
                        setModule((value ?? 'ism_forms') as Module);
                        setSubject(undefined);
                      }}
                    />
                    {module === 'ism_forms' ? (
                      <div className={classes.subjects}>
                        <Select
                          label={m('subject')}
                          w={280}
                          data={[
                            { value: '', label: m('subjectAll') },
                            ...ismSubjects.map((s) => ({ value: s, label: labeler(s) })),
                          ]}
                          value={subject ?? ''}
                          allowDeselect={false}
                          onChange={(value) => setSubject(value || undefined)}
                        />
                        {editable ? (
                          <>
                            <Button
                              variant="light"
                              leftSection={<IconPlus size={16} />}
                              onClick={() => setAddingSubject(true)}
                            >
                              {m('addSubject')}
                            </Button>
                            {subject ? (
                              <Button
                                variant="subtle"
                                color="red"
                                leftSection={<IconTrash size={16} />}
                                onClick={() => {
                                  setDraft(removeSubject(draft, subject));
                                  setSubject(undefined);
                                }}
                              >
                                {m('removeSubject')}
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    ) : (
                      <h2 className={classes.diffTitle}>{m(`moduleNames.${module}`)}</h2>
                    )}
                  </div>
                  <p className={classes.gridHelp}>
                    {editable ? m('gridHelp') : null}
                    {module === 'ism_forms' ? ` ${m('subjectHelp')}` : null}
                  </p>
                  <PermissionGrid
                    draft={draft}
                    roles={roles}
                    module={module}
                    subject={subject}
                    editable={editable}
                    onChange={(role, action, scope) => apply(role, action, scope)}
                  />
                  <ul className={classes.legend} aria-label={m('legend')}>
                    {SCOPE_CYCLE.map((scope) => (
                      <li key={scope}>
                        <span className={classes.scope} data-scope={scope}>
                          {m(`scopeShort.${scope}`)}
                        </span>{' '}
                        {m(`scope.${scope}`)}
                      </li>
                    ))}
                  </ul>
                </Panel>
              </div>
            </Tabs.Panel>
            <Tabs.Panel value="approvals">
              <Panel flush>
                <ApprovalRulesTable
                  rules={draft.rules}
                  roles={roles}
                  editable={editable}
                  subjectLabel={labeler}
                  onAdd={() => setRule({ index: null })}
                  onEdit={(index) => setRule({ index })}
                  onRemove={(index) =>
                    setDraft({ ...draft, rules: draft.rules.filter((_, i) => i !== index) })
                  }
                />
              </Panel>
            </Tabs.Panel>
            <Tabs.Panel value="history">
              <HistoryTab source={source} roles={roles} current={data.version} labeler={labeler} />
            </Tabs.Panel>
          </Tabs>
        </div>
      )}
      {data && editable ? (
        <div className={classes.mobileActions}>
          <Button
            variant="default"
            disabled={changes === 0}
            onClick={() => setDraft(draftFrom(data))}
          >
            {m('summary.discard')}
          </Button>
          <Button
            disabled={changes === 0}
            onClick={() => setSaving({ conflict: false, busy: false, error: null })}
          >
            {m('summary.save')}
          </Button>
        </div>
      ) : null}
      {justify ? (
        <JustificationDialog
          role={roleLabel(t, justify.role)}
          action={m(`actions.${justify.action}`)}
          onClose={() => setJustify(null)}
          onSave={(text) => {
            apply(justify.role, justify.action, justify.scope, text);
            setJustify(null);
          }}
        />
      ) : null}
      {addingSubject && draft ? (
        <SubjectDialog
          subjects={subjects}
          used={draft.subjects}
          onClose={() => setAddingSubject(false)}
          onAdd={(value) => {
            setDraft(addSubject(draft, value));
            setSubject(value);
            setAddingSubject(false);
          }}
        />
      ) : null}
      {rule && draft ? (
        <ApprovalRuleDialog
          rule={rule.index === null ? null : (draft.rules[rule.index] ?? null)}
          roles={roles}
          subjects={
            subjects
              ? [
                  ...subjects.folders.map((f) => ({
                    value: `folder:${f.key}`,
                    label: labeler(`folder:${f.key}`),
                  })),
                  ...subjects.documents.map((d) => ({
                    value: `document:${d.id}`,
                    label: labeler(`document:${d.id}`),
                  })),
                ]
              : []
          }
          onClose={() => setRule(null)}
          onSave={(next: ApprovalRule) => {
            setDraft({
              ...draft,
              rules:
                rule.index === null
                  ? [...draft.rules, next]
                  : draft.rules.map((r, i) => (i === rule.index ? next : r)),
            });
            setRule(null);
          }}
        />
      ) : null}
      {saving && data && saveDiff ? (
        <Modal
          opened
          onClose={() => setSaving(null)}
          closeButtonProps={{ 'aria-label': t('setup.close') }}
          title={m('save.title')}
          size="lg"
        >
          {saving.conflict ? null : (
            <div className={classes.diffScroll}>
              <MatrixDiff
                roles={roles}
                permissions={saveDiff.permissions}
                approvals={saveDiff.approvals}
                subjectLabel={labeler}
              />
            </div>
          )}
          <SaveMatrixForm
            version={data.version + 1}
            changes={changes}
            conflict={saving.conflict}
            busy={saving.busy}
            error={saving.error}
            onCancel={() => setSaving(null)}
            onReload={() => {
              setSaving(null);
              void load();
            }}
            onSave={save}
          />
        </Modal>
      ) : null}
      {preview && data ? (
        <Modal
          opened
          onClose={() => setPreview(null)}
          closeButtonProps={{ 'aria-label': t('setup.close') }}
          title={m('import.title')}
          size="lg"
        >
          {preview.errors.length > 0 ? (
            <>
              <p className={classes.notice} data-tone="danger" role="alert">
                {m('import.errors')}
              </p>
              <ul className={classes.diffList}>
                {preview.errors.map((error) => (
                  <li
                    key={`${error.sheet}-${error.row}-${error.field}`}
                    className={classes.diffItem}
                    data-tone="danger"
                  >
                    <StatusBadge tone="danger">{error.code}</StatusBadge>
                    <span>
                      {m('import.errorRow', {
                        sheet: m(`import.sheets.${error.sheet}`),
                        row: error.row,
                        field: error.field,
                        code: error.code,
                      })}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : preview.problem ? (
            <p className={classes.notice} data-tone="danger" role="alert">
              {m('import.problem', { code: setupErrorText(t, preview.problem.code) })}
            </p>
          ) : (
            <>
              <p className={classes.muted}>{m('import.ready')}</p>
              <MatrixDiff
                roles={roles}
                permissions={preview.diff.permissions}
                approvals={preview.diff.approvals}
                subjectLabel={labeler}
              />
            </>
          )}
          <div className={classes.dialogActions}>
            <Button variant="default" onClick={() => setPreview(null)}>
              {m('import.cancel')}
            </Button>
            {preview.errors.length === 0 && !preview.problem ? (
              <Button
                onClick={() => {
                  setDraft(draftFrom(preview));
                  setPreview(null);
                }}
              >
                {m('import.use')}
              </Button>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
